import { useState } from 'react';
import { api, ApiError, formatDateTime } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { publishQueueEvent } from '../../lib/queueBus';
import type { Office, QueuePriority, QueueTicket } from '../../lib/types';
import { ErrorNote, Field, StatusBadge } from '../../ui/components';

/**
 * c10-servicedesk exposes no GET/list endpoint for QueueTicket — only
 * POST /queue-tickets, POST /offices/:code/queue/next, POST
 * /queue-tickets/:id/serve and POST /queue-tickets/:id/no-show (see
 * packages/modules/c10-servicedesk/src/routes/queue.ts). So "the queue" this
 * panel shows is necessarily this browser session's own call history, built
 * up from those mutation responses — not a server-side live list. Reload the
 * page and the history is gone (the tickets themselves are of course still
 * safely in the database either way).
 */
export function QueuePanel({ office }: { office: Office }) {
  const { persona } = useAuth();
  const canOperate = persona?.roles.includes('COUNTER_OFFICER') ?? false;
  const [history, setHistory] = useState<QueueTicket[]>([]);
  const [callError, setCallError] = useState<string | null>(null);
  const [queueEmpty, setQueueEmpty] = useState(false);
  const [busy, setBusy] = useState(false);

  const [issuePriority, setIssuePriority] = useState<QueuePriority>('NORMAL');
  const [issueService, setIssueService] = useState('');
  const [issueMsg, setIssueMsg] = useState<string | null>(null);
  const [issueError, setIssueError] = useState<string | null>(null);
  const [issuing, setIssuing] = useState(false);

  const broadcast = (t: QueueTicket, status: 'CALLED' | 'SERVED' | 'NO_SHOW') =>
    publishQueueEvent({ officeCode: office.code, ticketId: t.id, number: t.number, priority: t.priority, status, at: new Date().toISOString() });

  const callNext = async () => {
    setBusy(true);
    setCallError(null);
    setQueueEmpty(false);
    try {
      const ticket = await api.post<QueueTicket>(`/api/v1/servicedesk/offices/${office.code}/queue/next`);
      setHistory((h) => [ticket, ...h].slice(0, 8));
      broadcast(ticket, 'CALLED');
    } catch (e) {
      if (e instanceof ApiError && e.code === 'QUEUE_EMPTY') {
        setQueueEmpty(true);
      } else {
        setCallError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not call the next ticket');
      }
    } finally {
      setBusy(false);
    }
  };

  const resolve = async (ticket: QueueTicket, action: 'serve' | 'no-show') => {
    setCallError(null);
    try {
      const updated = await api.post<QueueTicket>(`/api/v1/servicedesk/queue-tickets/${ticket.id}/${action}`);
      setHistory((h) => h.map((t) => (t.id === updated.id ? updated : t)));
      broadcast(updated, action === 'serve' ? 'SERVED' : 'NO_SHOW');
    } catch (e) {
      setCallError(e instanceof ApiError ? `${e.message} (${e.code})` : `Could not mark ticket as ${action}`);
    }
  };

  const issueTicket = async () => {
    setIssuing(true);
    setIssueError(null);
    setIssueMsg(null);
    try {
      const ticket = await api.post<QueueTicket>('/api/v1/servicedesk/queue-tickets', {
        officeCode: office.code,
        serviceCode: issueService || undefined,
        priority: issuePriority,
      });
      setIssueMsg(`Ticket ${ticket.number} issued (${ticket.priority.toLowerCase()} priority).`);
    } catch (e) {
      setIssueError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not issue a ticket');
    } finally {
      setIssuing(false);
    }
  };

  return (
    <div className="grid gap-5 lg:grid-cols-3">
      <div className="lg:col-span-2">
        <div className="card p-5">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-sm font-bold text-slate-700">Walk-in queue — {office.name}</h2>
            <button className="btn-primary" onClick={callNext} disabled={!canOperate || busy}>
              {busy ? 'Calling…' : 'Call next ticket'}
            </button>
          </div>

          {callError ? (
            <div className="mb-3">
              <ErrorNote message={callError} />
            </div>
          ) : null}
          {queueEmpty ? (
            <div className="mb-3 rounded-lg bg-slate-100 px-3.5 py-2.5 text-sm text-slate-600">
              No tickets waiting at {office.code} right now.
            </div>
          ) : null}

          {history.length === 0 ? (
            <div className="px-2 py-8 text-center text-sm text-slate-400">
              Nothing called yet this session. Press "Call next ticket" — priority order is ELDERLY/PWD first, then oldest NORMAL.
            </div>
          ) : (
            <ul className="space-y-2">
              {history.map((t) => (
                <li key={t.id} className="flex items-center justify-between rounded-lg border border-slate-200 px-3.5 py-2.5">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl font-black tabular-nums text-slate-900">{t.number}</span>
                    <StatusBadge status={t.priority} />
                    <StatusBadge status={t.status} />
                    {t.calledAt ? <span className="text-xs text-slate-400">called {formatDateTime(t.calledAt)}</span> : null}
                  </div>
                  {t.status === 'CALLED' ? (
                    <div className="flex gap-1.5">
                      <button className="btn-success !px-2.5 !py-1 text-xs" onClick={() => resolve(t, 'serve')} disabled={!canOperate}>
                        Mark served
                      </button>
                      <button className="btn-danger !px-2.5 !py-1 text-xs" onClick={() => resolve(t, 'no-show')} disabled={!canOperate}>
                        No-show
                      </button>
                    </div>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="card p-5">
        <h2 className="mb-3 text-sm font-bold text-slate-700">Issue a walk-in ticket</h2>
        <p className="mb-3 text-xs text-slate-500">
          For a citizen who can't take a ticket themselves (no kiosk, no phone). Open to any signed-in desk.
        </p>
        {issueError ? (
          <div className="mb-3">
            <ErrorNote message={issueError} />
          </div>
        ) : null}
        {issueMsg ? <div className="mb-3 rounded-lg bg-emerald-50 px-3.5 py-2.5 text-sm text-emerald-700">{issueMsg}</div> : null}
        <div className="space-y-3">
          <Field label="Service (optional)">
            <select className="field-input" value={issueService} onChange={(e) => setIssueService(e.target.value)}>
              <option value="">Not specified</option>
              {office.services.map((code) => (
                <option key={code} value={code}>
                  {code}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Priority">
            <select className="field-input" value={issuePriority} onChange={(e) => setIssuePriority(e.target.value as QueuePriority)}>
              <option value="NORMAL">Normal</option>
              <option value="ELDERLY">Elderly</option>
              <option value="PWD">Person with disability</option>
            </select>
          </Field>
          <button className="btn-primary w-full" onClick={issueTicket} disabled={issuing}>
            {issuing ? 'Issuing…' : 'Issue ticket'}
          </button>
        </div>
      </div>
    </div>
  );
}
